import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { 
  Upload, 
  FileText, 
  Key,
  CheckCircle,
  Clock,
  Package,
  AlertTriangle,
  X,
  Building2,
  Calendar,
  ShoppingCart,
  Trash2,
  Files,
  FileSpreadsheet
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [nfKey, setNfKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [multiPreview, setMultiPreview] = useState(null);
  const [multiFiles, setMultiFiles] = useState([]);
  const [selectedPreviews, setSelectedPreviews] = useState([]);
  const [storeCnpj, setStoreCnpj] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const [skipCnpjValidation, setSkipCnpjValidation] = useState(false);

  useEffect(() => {
    fetchInvoices();
    fetchStoreCnpj();
  }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/invoices`);
      setInvoices(res.data);
    } catch (error) {
      console.error("Error fetching invoices:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStoreCnpj = async () => {
    try {
      const res = await axios.get(`${API}/settings/store-cnpj`);
      setStoreCnpj(res.data.cnpj || "");
    } catch (error) {
      console.error("Error fetching store CNPJ:", error);
    }
  };

  const handleFilePreview = async (files) => {
    if (!files || files.length === 0) return;
    
    const xmlFiles = Array.from(files).filter(f => f.name.endsWith('.xml'));
    if (xmlFiles.length === 0) {
      toast.error("Arquivo inválido", {
        description: "Por favor, selecione arquivos XML de nota fiscal."
      });
      return;
    }

    setUploading(true);
    
    if (xmlFiles.length === 1) {
      // Single file preview
      const formData = new FormData();
      formData.append("file", xmlFiles[0]);

      try {
        const res = await axios.post(`${API}/invoices/preview-xml`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        
        setPreview(res.data);
        setPreviewFile(xmlFiles[0]);
      } catch (error) {
        toast.error("Erro ao ler XML", {
          description: error.response?.data?.detail || "Erro ao processar arquivo XML"
        });
      }
    } else {
      // Multiple files preview
      const formData = new FormData();
      xmlFiles.forEach(file => formData.append("files", file));

      try {
        const res = await axios.post(`${API}/invoices/preview-multiple`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        
        setMultiPreview(res.data);
        setMultiFiles(xmlFiles);
        // Auto-select importable files
        setSelectedPreviews(
          res.data.previews
            .filter(p => p.can_import)
            .map(p => p.nf_key)
        );
      } catch (error) {
        toast.error("Erro ao ler XMLs", {
          description: error.response?.data?.detail || "Erro ao processar arquivos"
        });
      }
    }
    
    setUploading(false);
  };

  const handleConfirmImport = async () => {
    if (!previewFile) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("file", previewFile);

    try {
      const res = await axios.post(`${API}/invoices/upload-xml`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success("Nota fiscal importada!", {
        description: `${res.data.products_added} produtos adicionados, ${res.data.products_updated} atualizados.`
      });
      
      setPreview(null);
      setPreviewFile(null);
      fetchInvoices();
    } catch (error) {
      toast.error("Erro ao importar", {
        description: error.response?.data?.detail || "Erro ao processar arquivo XML"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmMultiImport = async () => {
    if (selectedPreviews.length === 0) {
      toast.error("Selecione ao menos uma nota para importar");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    
    // Only add selected files
    multiFiles.forEach(file => {
      const preview = multiPreview?.previews?.find(p => p.filename === file.name);
      if (preview && selectedPreviews.includes(preview.nf_key)) {
        formData.append("files", file);
      }
    });

    try {
      const res = await axios.post(`${API}/invoices/import-multiple`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success("Importação concluída!", {
        description: `${res.data.total_imported} notas importadas. ${res.data.total_products_added} produtos adicionados.`
      });
      
      setMultiPreview(null);
      setMultiFiles([]);
      setSelectedPreviews([]);
      fetchInvoices();
    } catch (error) {
      toast.error("Erro ao importar", {
        description: error.response?.data?.detail || "Erro ao processar arquivos"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteInvoice = async () => {
    if (!deleteConfirm) return;

    try {
      const res = await axios.delete(`${API}/invoices/${deleteConfirm.id}`);
      toast.success("Nota excluída!", {
        description: `${res.data.products_deleted} produtos removidos. Reprocesse as vendas no Estoque.`
      });
      setDeleteConfirm(null);
      fetchInvoices();
    } catch (error) {
      toast.error("Erro ao excluir", {
        description: error.response?.data?.detail || "Tente novamente"
      });
    }
  };

  const handleExcelUpload = async (file) => {
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error("Arquivo inválido", {
        description: "Por favor, selecione um arquivo Excel (.xlsx ou .xls)"
      });
      return;
    }

    setUploadingExcel(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await axios.post(`${API}/invoices/import-excel`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success("Base importada!", {
        description: `${res.data.invoices_created} notas, ${res.data.products_added} produtos novos, ${res.data.products_updated} atualizados.`
      });
      
      fetchInvoices();
    } catch (error) {
      toast.error("Erro ao importar", {
        description: error.response?.data?.detail || "Erro ao processar arquivo"
      });
    } finally {
      setUploadingExcel(false);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    handleFilePreview(e.dataTransfer.files);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const handleKeyImport = async () => {
    if (!nfKey || nfKey.length !== 44) {
      toast.error("Chave inválida", {
        description: "A chave de acesso deve ter 44 dígitos."
      });
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/invoices/import-by-key`, { nf_key: nfKey });
      toast.success("Chave registrada", {
        description: res.data.message
      });
      setNfKey("");
      fetchInvoices();
    } catch (error) {
      toast.error("Erro", {
        description: error.response?.data?.detail || "Erro ao registrar chave"
      });
    } finally {
      setLoading(false);
    }
  };

  const togglePreviewSelection = (nfKey) => {
    setSelectedPreviews(prev => 
      prev.includes(nfKey) 
        ? prev.filter(k => k !== nfKey)
        : [...prev, nfKey]
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatCNPJ = (cnpj) => {
    if (!cnpj) return "-";
    const clean = cnpj.replace(/\D/g, '');
    if (clean.length !== 14) return cnpj;
    return clean.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  };

  return (
    <div className="p-8 animate-fadeIn" data-testid="invoices-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight">Notas Fiscais</h1>
        <p className="text-muted-foreground">Importe notas fiscais para atualizar o estoque</p>
      </div>

      <Tabs defaultValue="xml" className="mb-8">
        <TabsList>
          <TabsTrigger value="xml" data-testid="tab-xml">
            <Upload className="w-4 h-4 mr-2" />
            Upload XML
          </TabsTrigger>
          <TabsTrigger value="excel" data-testid="tab-excel">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Base Excel
          </TabsTrigger>
          <TabsTrigger value="key" data-testid="tab-key">
            <Key className="w-4 h-4 mr-2" />
            Chave de Acesso
          </TabsTrigger>
        </TabsList>

        <TabsContent value="xml" className="mt-4">
          <Card>
            <CardContent className="pt-6">
              <div
                className={`upload-zone ${dragOver ? 'dragover' : ''}`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => document.getElementById('file-input').click()}
                data-testid="upload-zone"
              >
                <input
                  id="file-input"
                  type="file"
                  accept=".xml"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFilePreview(e.target.files)}
                />
                <Files className="w-12 h-12 mx-auto mb-4 text-muted-foreground" strokeWidth={1} />
                <p className="text-lg font-medium mb-1">
                  {uploading ? "Lendo XMLs..." : "Arraste os arquivos XML aqui"}
                </p>
                <p className="text-sm text-muted-foreground">
                  ou clique para selecionar (múltiplos arquivos permitidos)
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="excel" className="mt-4">
          <Card>
            <CardContent className="pt-6">
              <div
                className={`upload-zone`}
                onClick={() => document.getElementById('excel-input').click()}
                data-testid="excel-upload-zone"
              >
                <input
                  id="excel-input"
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={(e) => handleExcelUpload(e.target.files[0])}
                />
                <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 text-muted-foreground" strokeWidth={1} />
                <p className="text-lg font-medium mb-1">
                  {uploadingExcel ? "Importando base..." : "Importar Base de Compras"}
                </p>
                <p className="text-sm text-muted-foreground">
                  Arquivo Excel com aba &quot;Database_Compras&quot;
                </p>
              </div>
              <div className="mt-4 p-3 bg-muted/30 rounded-sm text-sm">
                <p className="font-medium mb-1">Formato esperado:</p>
                <ul className="text-muted-foreground space-y-1 text-xs">
                  <li>• Aba: <code className="bg-muted px-1">Database_Compras</code></li>
                  <li>• Colunas: codigo_gtin, descricao, normalizado_qtd_comercial, normalizado_valor_unitario</li>
                  <li>• Agrupa automaticamente por chave da NF</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="key" className="mt-4">
          <Card>
            <CardContent className="pt-6">
              <div className="max-w-xl">
                <label className="block text-sm font-medium mb-2">
                  Chave de Acesso da NF-e (44 dígitos)
                </label>
                <div className="flex gap-3">
                  <Input
                    value={nfKey}
                    onChange={(e) => setNfKey(e.target.value.replace(/\D/g, '').slice(0, 44))}
                    placeholder="Digite a chave de acesso..."
                    className="font-mono text-sm"
                    data-testid="nf-key-input"
                  />
                  <Button 
                    onClick={handleKeyImport}
                    disabled={loading || nfKey.length !== 44}
                    data-testid="import-key-btn"
                  >
                    {loading ? "Importando..." : "Importar"}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {nfKey.length}/44 dígitos
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Invoices List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5" strokeWidth={1.5} />
            Notas Importadas ({invoices.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 text-center text-muted-foreground">Carregando...</div>
          ) : invoices.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="data-table" data-testid="invoices-table">
                <thead>
                  <tr>
                    <th>Chave de Acesso</th>
                    <th>Fornecedor</th>
                    <th>Emissão</th>
                    <th>Itens</th>
                    <th>Valor Total</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.id}>
                      <td className="font-mono text-xs max-w-[180px] truncate" title={inv.nf_key}>
                        {inv.nf_key?.slice(0, 20)}...
                      </td>
                      <td className="max-w-[150px] truncate">{inv.supplier || "-"}</td>
                      <td className="text-sm whitespace-nowrap">{formatDate(inv.emission_date)}</td>
                      <td className="font-mono">{inv.items_count}</td>
                      <td className="font-mono">{formatCurrency(inv.total_value)}</td>
                      <td>
                        <Badge 
                          variant={inv.status === 'imported' ? 'default' : 'secondary'}
                          className="text-xs"
                        >
                          {inv.status === 'imported' ? 'Importada' : 'Pendente'}
                        </Badge>
                      </td>
                      <td>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteConfirm(inv)}
                          className="text-destructive hover:text-destructive"
                          data-testid={`delete-${inv.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Nenhuma nota fiscal importada</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Single Preview Dialog */}
      <Dialog open={!!preview} onOpenChange={() => { setPreview(null); setPreviewFile(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Prévia da Nota Fiscal
            </DialogTitle>
          </DialogHeader>
          
          {preview && (
            <div className="space-y-4">
              {!preview.cnpj_match && (
                <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium text-amber-800">CNPJ diferente do cadastro</p>
                      <p className="text-sm text-amber-700 mt-1">
                        Nota emitida para: <strong>{formatCNPJ(preview.dest_cnpj)}</strong>
                      </p>
                      <div className="flex items-center gap-2 mt-3">
                        <Checkbox 
                          id="skip-cnpj"
                          checked={skipCnpjValidation}
                          onCheckedChange={setSkipCnpjValidation}
                        />
                        <label htmlFor="skip-cnpj" className="text-sm text-amber-800 cursor-pointer">
                          Importar mesmo assim (ignorar validação de CNPJ)
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {preview.cnpj_match && (
                <div className="bg-green-50 border border-green-200 rounded-sm p-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <p className="font-medium text-green-800">CNPJ verificado ✓</p>
                  </div>
                </div>
              )}

              {preview.already_imported && (
                <div className="bg-red-50 border border-red-200 rounded-sm p-4">
                  <div className="flex items-center gap-3">
                    <X className="w-5 h-5 text-red-600" />
                    <p className="font-medium text-red-800">Nota já importada</p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-muted/30 p-3 rounded-sm">
                  <p className="text-xs text-muted-foreground">Fornecedor</p>
                  <p className="font-medium">{preview.supplier || "-"}</p>
                </div>
                <div className="bg-muted/30 p-3 rounded-sm">
                  <p className="text-xs text-muted-foreground">Destinatário</p>
                  <p className="font-medium">{preview.dest_name || "-"}</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="bg-muted/30 p-3 rounded-sm">
                  <p className="text-xs text-muted-foreground">Emissão</p>
                  <p className="font-mono text-sm">{formatDate(preview.emission_date)}</p>
                </div>
                <div className="bg-muted/30 p-3 rounded-sm">
                  <p className="text-xs text-muted-foreground">Itens</p>
                  <p className="font-mono text-sm">{preview.total_products} produtos</p>
                </div>
                <div className="bg-muted/30 p-3 rounded-sm">
                  <p className="text-xs text-muted-foreground">Valor Total</p>
                  <p className="font-mono text-sm font-medium">{formatCurrency(preview.total_value)}</p>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium mb-2">Produtos (primeiros 10)</p>
                <div className="max-h-[150px] overflow-y-auto border rounded-sm">
                  <table className="data-table text-xs">
                    <thead className="sticky top-0 bg-white">
                      <tr>
                        <th>EAN</th>
                        <th>Produto</th>
                        <th>Qtd</th>
                        <th>Custo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.products?.map((p, idx) => (
                        <tr key={idx}>
                          <td className="font-mono">{p.ean}</td>
                          <td className="max-w-[150px] truncate">{p.name}</td>
                          <td className="font-mono">{p.quantity}</td>
                          <td className="font-mono">{formatCurrency(p.cost_price)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setPreview(null); setPreviewFile(null); setSkipCnpjValidation(false); }}>
              Cancelar
            </Button>
            <Button 
              onClick={handleConfirmImport}
              disabled={uploading || preview?.already_imported || (!preview?.cnpj_match && !skipCnpjValidation)}
              className={!preview?.cnpj_match && !preview?.already_imported && skipCnpjValidation ? 'bg-amber-600 hover:bg-amber-700' : ''}
            >
              {uploading ? "Importando..." : (!preview?.cnpj_match && skipCnpjValidation) ? "Importar (sem validação CNPJ)" : "Importar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Multiple Preview Dialog */}
      <Dialog open={!!multiPreview} onOpenChange={() => { setMultiPreview(null); setMultiFiles([]); setSelectedPreviews([]); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Files className="w-5 h-5" />
              Importar Múltiplas Notas ({multiPreview?.total_files || 0} arquivos)
            </DialogTitle>
            <DialogDescription>
              Selecione as notas que deseja importar
            </DialogDescription>
          </DialogHeader>
          
          {multiPreview && (
            <div className="space-y-4">
              <div className="flex items-center justify-between text-sm">
                <span>{selectedPreviews.length} selecionadas de {multiPreview.importable} importáveis</span>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setSelectedPreviews(
                    multiPreview.previews.filter(p => p.can_import).map(p => p.nf_key)
                  )}
                >
                  Selecionar todas
                </Button>
              </div>

              <div className="border rounded-sm divide-y max-h-[400px] overflow-y-auto">
                {multiPreview.previews?.map((p, idx) => (
                  <div 
                    key={idx} 
                    className={`p-3 flex items-center gap-3 ${!p.can_import ? 'bg-muted/50' : ''}`}
                  >
                    <Checkbox
                      checked={selectedPreviews.includes(p.nf_key)}
                      onCheckedChange={() => togglePreviewSelection(p.nf_key)}
                      disabled={!p.can_import}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{p.supplier || p.filename}</span>
                        {!p.cnpj_match && p.can_import && (
                          <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-700">
                            CNPJ diferente
                          </Badge>
                        )}
                        {p.already_imported && (
                          <Badge variant="secondary" className="text-xs">Já importada</Badge>
                        )}
                        {p.error && (
                          <Badge variant="destructive" className="text-xs">Erro</Badge>
                        )}
                      </div>
                      <div className="flex gap-4 text-xs text-muted-foreground mt-1">
                        <span>{formatDate(p.emission_date)}</span>
                        <span>{p.total_products} itens</span>
                        <span className="font-mono">{formatCurrency(p.total_value)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setMultiPreview(null); setMultiFiles([]); setSelectedPreviews([]); }}>
              Cancelar
            </Button>
            <Button 
              onClick={handleConfirmMultiImport}
              disabled={uploading || selectedPreviews.length === 0}
            >
              {uploading ? "Importando..." : `Importar ${selectedPreviews.length} Notas`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="w-5 h-5" />
              Excluir Nota Fiscal
            </DialogTitle>
          </DialogHeader>
          
          {deleteConfirm && (
            <div className="space-y-4">
              <p>Tem certeza que deseja excluir esta nota fiscal?</p>
              <div className="bg-muted/30 p-3 rounded-sm">
                <p className="text-sm"><strong>Fornecedor:</strong> {deleteConfirm.supplier}</p>
                <p className="text-sm"><strong>Valor:</strong> {formatCurrency(deleteConfirm.total_value)}</p>
                <p className="text-sm"><strong>Itens:</strong> {deleteConfirm.items_count}</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
                <p className="text-sm text-amber-800">
                  <strong>Atenção:</strong> Os produtos desta nota serão removidos do estoque. 
                  Após excluir, vá em <strong>Estoque → Processar Vendas</strong> para recalcular.
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDeleteInvoice}>
              Excluir Nota
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Invoices;
